import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default function GlobalNotificationSystem() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation(['notifications']);
    // Track the latest notification ID seen to avoid re-toasting
    const latestIdRef = useRef<number | null>(null);
    const initialized = useRef(false);

    useEffect(() => {
        if (!isAuthenticated) return;

        const checkNotifications = async () => {
            try {
                // Fetch latest notifications (limit 5)
                const list = await api.notifications.list(5, 0);
                if (!Array.isArray(list) || list.length === 0) {
                    if (!initialized.current) {
                        initialized.current = true;
                        latestIdRef.current = 0;
                    }
                    return;
                }

                // Assuming list is sorted by created_at DESC (newest first)
                const newest = list[0];

                if (!initialized.current) {
                    // First run: Initialize reference. Do NOT toast historical notifications.
                    latestIdRef.current = newest.id;
                    initialized.current = true;
                    return;
                }

                const translateNotification = (notif: any) => {
                    const titleKey = `notifications:items.${notif.type}.title`;
                    const msgKey = `notifications:items.${notif.type}.message`;

                    if (!i18n.exists(titleKey)) {
                        return { title: notif.title, message: notif.message };
                    }

                    const interpolationData = {
                        ...notif.data,
                        tracking_info: notif.data?.tracking_number ? `\n${t('notifications:items.common.tracking_number', 'Numéro de suivi')} : ${notif.data.tracking_number}` : '',
                        defaultValue: notif.message,
                        message: notif.message
                    };

                    const translatedTitle = t(titleKey, interpolationData) as string;
                    const translatedMessage = t(msgKey, interpolationData) as string;

                    return { title: translatedTitle, message: translatedMessage };
                };

                if (latestIdRef.current !== null && newest.id > latestIdRef.current) {
                    // Identify New Items
                    const newItems = list.filter((n: any) => n.id > latestIdRef.current!);

                    // Toast them (Oldest new item first)
                    [...newItems].reverse().forEach((n: any) => {
                        const { message } = translateNotification(n);

                        const handleToastClick = (t: any) => {
                            if (n.link) {
                                navigate(n.link);
                                toast.dismiss(t.id);
                            }
                        };

                        const toastOptions = {
                            duration: 5000,
                            style: n.link ? { cursor: 'pointer' } : {},
                        };

                        if (n.type === 'SYSTEM_ALERT') {
                            toast((t) => (
                                <div onClick={() => handleToastClick(t)}>{message}</div>
                            ), {
                                ...toastOptions,
                                icon: '🔔',
                                style: { ...toastOptions.style, background: '#1e293b', color: '#fff', border: '1px solid #3b82f6' }
                            });
                        } else if (n.type === 'TOURNAMENT_JOINED') {
                            toast((t) => (
                                <div onClick={() => handleToastClick(t)}>{message}</div>
                            ), { ...toastOptions, icon: '🎾' });
                        } else if (n.type === 'DISCOVERY_MATCH') {
                            toast((t) => (
                                <div onClick={() => handleToastClick(t)}>{message}</div>
                            ), { ...toastOptions, icon: '🔥' });
                        } else {
                            // Any other notification type
                            toast((t) => (
                                <div onClick={() => handleToastClick(t)}>{message}</div>
                            ), toastOptions);
                        }
                    });

                    latestIdRef.current = newest.id;
                }
            } catch (e) {
                console.error("Global Notification System Error:", e);
            }
        };

        // Initial check immediately
        checkNotifications();

        // Poll interval (every 10 seconds for responsiveness)
        const interval = setInterval(checkNotifications, 60000); // 60s (Optimized)
        return () => clearInterval(interval);

    }, [isAuthenticated]);

    return null;
}
